import { CoreEnv } from '@eventbrite/context-gen';
import { sdkRequest } from '@eventbrite/http';
import { gettext } from '@eventbrite/i18n';
import { initializeRealUserMonitoring } from '@eventbrite/real-user-monitoring';
import { trackEventFromState } from '@eventbrite/site-analytics';
import { SiteStructure } from '@eventbrite/site-structure';
import React, { useState } from 'react';
import './app.scss';
import SearchResults from './components/SearchResults';
import TimeframeNavList from './components/TimeframeNavList';
import TrendingSearches from './components/TrendingSearches';
import { Timeframe, TrendingProps } from './types';
import { formatPageTitle, formatTrendingSearchPageTitle } from './utils';

const httpRefererWasSearchEngine = (httpReferer: string) =>
    httpReferer.search('google|bing|yahoo') >= 0;

const isProd = (env: CoreEnv) => {
    return env.ebDomain.indexOf('eventbrite') > -1;
};

const TrendingApp = (props: TrendingProps) => {
    const [activeTimeframe, setActiveTimeframe] = useState(
        props.active_timeframe || ('month' as Timeframe),
    );
    const [trendingSearchResults, updateTrendingSearchResults] = useState({
        [props.active_timeframe || ('month' as Timeframe)]:
            props.trending_searches,
    });

    const {
        env,
        request,
        user,
        footerLinks,
        location_string_for_url: locationStringForURL,
        display_location: displayLocation,
        featureFlags,
        trending_searches: trendingSearches,
    } = props;

    const pageTitle = featureFlags.launchConsolidateAndExpandTrendingSearches
        ? formatTrendingSearchPageTitle(displayLocation)
        : formatPageTitle(displayLocation, activeTimeframe);

    if (isProd(env)) {
        initializeRealUserMonitoring({
            sampleRate: 100,
            version: props.app_version,
            service: props.app_name,
        });
    }

    const _handleSearchResultClick = (searchTerm: string) => {
        const { http_referer: httpReferer } = props;

        if (httpRefererWasSearchEngine(httpReferer)) {
            trackEventFromState(props, {
                category: 'trending-searches',
                action: 'ViewTrendingSearch',
                label: searchTerm,
            });
        }
    };

    const _handleClick = (timeframe: Timeframe, location: string) => {
        const { http_referer: httpReferer } = props;

        if (httpRefererWasSearchEngine(httpReferer)) {
            // Track date range switch
            trackEventFromState(props, {
                category: 'trending-searches',
                action: 'AdjustDateRange',
                label: timeframe,
            });
        }

        // Does the current timeframe data already exist in state?
        if (trendingSearchResults[timeframe]) {
            setActiveTimeframe(timeframe);
        } else {
            const url = `/api/v3/trending/?location=${location}&timeframe=${timeframe}`;

            // fetch API data
            sdkRequest(url).then(
                (response: { trending: { trending_searches: string[] } }) => {
                    // stuff data into store
                    updateTrendingSearchResults({
                        ...trendingSearchResults,
                        [timeframe]: response.trending.trending_searches,
                    });
                    setActiveTimeframe(timeframe);
                },
            );
        }
    };

    return (
        <SiteStructure
            env={env}
            user={user}
            request={request}
            footerLinks={footerLinks}
            pageTitle={pageTitle}
            featureFlags={featureFlags}
        >
            <h1 className="eds-show-up-md eds-text-hm eds-l-pad-top-5">
                {pageTitle}
            </h1>
            <h2 className="eds-text-bl eds-l-pad-bot-5 eds-l-sm-mar-top-5 eds-l-md-mar-top-5">
                {displayLocation === 'Online'
                    ? gettext(
                          'Browse around and see what events are happening online',
                      )
                    : gettext(
                          'Browse around and see what events are happening in your city.',
                      )}
            </h2>

            {!featureFlags.launchConsolidateAndExpandTrendingSearches && (
                <>
                    <TimeframeNavList
                        activeTimeframe={activeTimeframe}
                        location={locationStringForURL}
                        onClick={_handleClick}
                    />

                    <SearchResults
                        searches={trendingSearchResults[activeTimeframe]}
                        location={locationStringForURL}
                        displayLocation={displayLocation}
                        activeTimeframe={activeTimeframe}
                        shouldNoFollowLinks={props.shouldNoFollowLinks}
                        onClick={_handleSearchResultClick}
                    />
                </>
            )}

            {featureFlags.launchConsolidateAndExpandTrendingSearches && (
                <TrendingSearches
                    searches={trendingSearches}
                    location={locationStringForURL}
                    displayLocation={displayLocation}
                    shouldNoFollowLinks={props.shouldNoFollowLinks}
                    onClick={_handleSearchResultClick}
                />
            )}
        </SiteStructure>
    );
};

export default TrendingApp;
